import { useState } from 'react';
import { apiErrorMessage } from '@/lib/conflict';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { DataState } from '@/components/common/DataState';

interface HealthCheck {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  latencyMs: number;
  details?: string;
}

interface HealthData {
  overall: 'healthy' | 'degraded' | 'down';
  checks: HealthCheck[];
}

interface DependencyStatus {
  id: string;
  name: string;
  status: 'healthy' | 'degraded' | 'down' | 'not_configured';
  latencyMs: number;
  details?: string;
  meta?: Record<string, string | number>;
}

interface DependencySnapshot {
  checkedAt: string;
  deps: DependencyStatus[];
}

interface CronJobSummary {
  name: string;
  runs: number;
  okCount: number;
  failedCount: number;
  runningCount: number;
  lastStartedAt?: string;
  avgDurationMs?: number | null;
  lastOkAt?: string | null;
  lastOkStats?: Record<string, number> | null;
  lastFailAt?: string | null;
  lastFailError?: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  healthy: 'bg-green-500',
  degraded: 'bg-yellow-500',
  down: 'bg-red-500',
  not_configured: 'bg-gray-300',
};

const STATUS_BG: Record<string, string> = {
  healthy: 'bg-green-50 text-green-800 border-green-200',
  degraded: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  down: 'bg-red-50 text-red-800 border-red-200',
};

/**
 * A job's health from its summary row. 'failing' = the most recent outcome was
 * a failure OR the last "ok" run did zero work with errors (the silent-outage
 * shape); 'stuck' = a run started hours ago and never finished.
 */
/** One rota job, as /admin/cron/jobs describes it. */
interface RunnableJob {
  name: string;
  hour: number;
  day: number | null;
  cadence: 'daily' | 'weekly' | 'monthly';
}

function cronJobHealth(job: CronJobSummary): 'ok' | 'errors' | 'failing' | 'stuck' {
  const lastOk = job.lastOkAt ? Date.parse(job.lastOkAt) : 0;
  const lastFail = job.lastFailAt ? Date.parse(job.lastFailAt) : 0;
  const lastStart = job.lastStartedAt ? Date.parse(job.lastStartedAt) : 0;
  // Stuck = the MOST RECENT start never completed (>3h and no ok/fail since).
  // runningCount alone is not enough — old zombie rows linger in the 7-day
  // window even after later runs completed fine.
  const lastDone = Math.max(lastOk, lastFail);
  if (
    job.runningCount > 0 &&
    lastStart > 0 &&
    lastStart > lastDone &&
    Date.now() - lastStart > 3 * 3600_000
  ) {
    return 'stuck';
  }
  if (lastFail > lastOk) return 'failing';
  const stats = job.lastOkStats || {};
  const errors = Object.entries(stats)
    .filter(([k]) => /error|fail/i.test(k))
    .reduce((a, [, v]) => a + (typeof v === 'number' ? v : 0), 0);
  if (errors > 0) {
    const successes = Object.entries(stats)
      .filter(
        ([k]) =>
          !/error|fail|^total$|^eligible$|skip|project|scanned|considered|seen|checked|duration/i.test(
            k
          )
      )
      .reduce((a, [, v]) => a + (typeof v === 'number' ? v : 0), 0);
    return successes === 0 ? 'failing' : 'errors';
  }
  return 'ok';
}

const JOB_HEALTH_BADGE: Record<string, string> = {
  ok: 'bg-green-50 text-green-700 border-green-200',
  errors: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  failing: 'bg-red-50 text-red-700 border-red-200',
  stuck: 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function SREDashboardPage() {
  const queryClient = useQueryClient();

  // Live dashboard: refetch every 4 minutes (same cadence as the old
  // setInterval), keeping the last numbers on screen during background refetch.
  const healthQuery = useQuery({
    queryKey: qk.sre(),
    queryFn: async (): Promise<HealthData> => {
      // Use the base URL without /api/v1 prefix for health endpoint
      const baseUrl = (api.defaults.baseURL ?? '').replace('/api/v1', '').replace('/api', '');
      const res = await fetch(`${baseUrl}/health/detailed`);
      const data = await res.json();
      return data.data as HealthData;
    },
    refetchInterval: 240000, // Refresh every 4 minutes
  });

  // External vendors: paints from the server-side cached snapshot; the
  // "Probe now" button re-checks every vendor live (real API calls).
  const depsQuery = useQuery({
    queryKey: qk.sreDependencies(),
    queryFn: async (): Promise<DependencySnapshot> => {
      const res = await api.get('/admin/health/dependencies');
      return res.data.data as DependencySnapshot;
    },
  });

  const refreshDeps = useMutation({
    mutationFn: async (): Promise<DependencySnapshot> => {
      const res = await api.post('/admin/health/dependencies/refresh');
      return res.data.data as DependencySnapshot;
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(qk.sreDependencies(), fresh);
    },
  });

  const cronQuery = useQuery({
    queryKey: qk.sreCronSummary(),
    queryFn: async (): Promise<CronJobSummary[]> => {
      const res = await api.get('/admin/cron/summary');
      return res.data.data as CronJobSummary[];
    },
    refetchInterval: 240000,
  });

  // Which rota jobs can be run by hand. Comes from the server so the button
  // never appears for a job the backend would reject (the rota is the one
  // source of truth; a hardcoded list here would drift the first time a job
  // is added).
  const cronJobsQuery = useQuery({
    queryKey: qk.sreCronJobs(),
    queryFn: async (): Promise<RunnableJob[]> => {
      const res = await api.get('/admin/cron/jobs');
      return res.data.data.jobs as RunnableJob[];
    },
  });
  const runnable = new Map((cronJobsQuery.data ?? []).map((j) => [j.name, j]));

  const [runResult, setRunResult] = useState<{
    name: string;
    ok: boolean;
    text: string;
  } | null>(null);

  const runJob = useMutation({
    mutationFn: async (name: string) => {
      const res = await api.post('/admin/cron/run', { name });
      return { name, stats: res.data.data.stats, durationMs: res.data.data.durationMs };
    },
    onSuccess: ({ name, stats, durationMs }) => {
      const summary = stats
        ? Object.entries(stats)
            .map(([k, v]) => `${k}:${v}`)
            .join(' ')
        : '(no stats)';
      setRunResult({
        name,
        ok: true,
        text: `finished in ${(durationMs / 1000).toFixed(1)}s — ${summary}`,
      });
      // The run it just made is now history: refresh both views.
      queryClient.invalidateQueries({ queryKey: qk.sreCronSummary() });
      queryClient.invalidateQueries({ queryKey: qk.sreCronJobs() });
    },
    onError: (err, name) => {
      setRunResult({ name, ok: false, text: apiErrorMessage(err, 'Job failed') });
    },
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">SRE Dashboard</h1>
          <p className="text-gray-500">Infrastructure health and monitoring</p>
        </div>
        <div className="text-right">
          <button
            onClick={() => healthQuery.refetch()}
            disabled={healthQuery.isFetching}
            className="rounded-md bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200 disabled:opacity-50"
          >
            Refresh
          </button>
          {healthQuery.dataUpdatedAt > 0 && (
            <p className="mt-1 text-xs text-gray-400">
              Last checked: {new Date(healthQuery.dataUpdatedAt).toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      <DataState query={healthQuery}>
        {(health) => (
          <>
            {/* Overall Status */}
            <div className={`rounded-lg border p-6 ${STATUS_BG[health.overall]}`}>
              <div className="flex items-center gap-3">
                <div className={`h-4 w-4 rounded-full ${STATUS_COLORS[health.overall]}`} />
                <h2 className="text-xl font-bold capitalize">System {health.overall}</h2>
              </div>
              <p className="mt-1 text-sm opacity-75">
                {health.checks.filter((c) => c.status === 'healthy').length} of{' '}
                {health.checks.length} services operational
              </p>
            </div>

            {/* Individual Checks */}
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {health.checks.map((check) => (
                <div key={check.name} className="rounded-lg border p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{check.name}</h3>
                    <div className={`h-3 w-3 rounded-full ${STATUS_COLORS[check.status]}`} />
                  </div>
                  <p className="mt-2 text-sm capitalize text-gray-500">{check.status}</p>
                  <p className="text-sm text-gray-400">{check.latencyMs}ms latency</p>
                  {check.details && <p className="mt-1 text-xs text-red-500">{check.details}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </DataState>

      {/* External Dependencies — vendor APIs, credentials, credits/balances */}
      <div className="rounded-lg border p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">External Dependencies</h2>
            <p className="text-sm text-gray-500">
              Vendor APIs the platform features depend on — including credit/balance state
            </p>
          </div>
          <div className="text-right">
            <button
              onClick={() => refreshDeps.mutate()}
              disabled={refreshDeps.isPending}
              className="rounded-md bg-gray-900 px-3 py-2 text-sm text-white hover:bg-gray-700 disabled:opacity-50"
            >
              {refreshDeps.isPending ? 'Probing…' : 'Probe now'}
            </button>
            {depsQuery.data?.checkedAt && (
              <p className="mt-1 text-xs text-gray-400">
                Last probed: {new Date(depsQuery.data.checkedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <DataState query={depsQuery}>
          {(snapshot) => (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {snapshot.deps.map((dep) => (
                <div
                  key={dep.id}
                  className={`rounded-lg border p-4 ${dep.status === 'down' ? 'border-red-300 bg-red-50/50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{dep.name}</h3>
                    <div className={`h-3 w-3 rounded-full ${STATUS_COLORS[dep.status]}`} />
                  </div>
                  <p className="mt-1 text-sm capitalize text-gray-500">
                    {dep.status.replace('_', ' ')}
                    {dep.latencyMs > 0 && ` · ${dep.latencyMs}ms`}
                  </p>
                  {dep.meta && (
                    <dl className="mt-2 space-y-0.5 text-xs text-gray-600">
                      {Object.entries(dep.meta).map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-2">
                          <dt className="text-gray-400">{k}</dt>
                          <dd className="font-mono">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {dep.details && (
                    <p
                      className={`mt-2 text-xs ${dep.status === 'down' ? 'text-red-600' : 'text-gray-400'}`}
                    >
                      {dep.details}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DataState>
      </div>

      {/* Scheduled Jobs — per-job last outcome incl. silent-failure detection */}
      <div className="rounded-lg border p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Scheduled Jobs</h2>
          <p className="text-sm text-gray-500">
            Last 7 days per cron job. "Failing" includes runs that finished "ok" while every unit of
            work inside errored.
          </p>
        </div>
        {runResult && (
          <div
            className={`mb-3 rounded border px-3 py-2 text-xs ${
              runResult.ok
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            <span className="font-mono">{runResult.name}</span> {runResult.text}
          </div>
        )}
        <DataState query={cronQuery}>
          {(jobs) => (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-4">Job</th>
                    <th className="py-2 pr-4">Health</th>
                    <th className="py-2 pr-4">Last run</th>
                    <th className="py-2 pr-4">Last success</th>
                    <th className="py-2 pr-4">Last ok stats</th>
                    <th className="py-2 pr-4">Last failure</th>
                    <th className="py-2">Run</th>
                  </tr>
                </thead>
                <tbody>
                  {[...jobs]
                    .sort((a, b) => {
                      const rank = { failing: 0, stuck: 1, errors: 2, ok: 3 } as const;
                      return rank[cronJobHealth(a)] - rank[cronJobHealth(b)];
                    })
                    .map((job) => {
                      const health = cronJobHealth(job);
                      return (
                        <tr key={job.name} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-mono text-xs">{job.name}</td>
                          <td className="py-2 pr-4">
                            <span
                              className={`inline-block rounded-full border px-2 py-0.5 text-xs capitalize ${JOB_HEALTH_BADGE[health]}`}
                            >
                              {health}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-xs text-gray-500">
                            {job.lastStartedAt ? new Date(job.lastStartedAt).toLocaleString() : '—'}
                          </td>
                          <td className="py-2 pr-4 text-xs text-gray-500">
                            {job.lastOkAt ? new Date(job.lastOkAt).toLocaleString() : '—'}
                          </td>
                          <td className="py-2 pr-4 text-xs text-gray-500">
                            {job.lastOkStats
                              ? Object.entries(job.lastOkStats)
                                  .map(([k, v]) => `${k}:${v}`)
                                  .join(' ')
                              : '—'}
                          </td>
                          <td className="py-2 pr-4 text-xs text-red-500">
                            {job.lastFailError
                              ? `${job.lastFailAt ? new Date(job.lastFailAt).toLocaleDateString() : ''} ${job.lastFailError.slice(0, 80)}`
                              : '—'}
                          </td>
                          <td className="py-2 text-xs">
                            {runnable.has(job.name) ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setRunResult(null);
                                  runJob.mutate(job.name);
                                }}
                                disabled={runJob.isPending}
                                className="rounded border px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50"
                                title={`Runs ${job.name} now, across every project it applies to`}
                              >
                                {runJob.isPending && runJob.variables === job.name
                                  ? 'Running…'
                                  : 'Run now'}
                              </button>
                            ) : (
                              <span
                                className="text-gray-300"
                                title="Runs every tick; not manually triggerable"
                              >
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </DataState>
      </div>

      {/* Quick Links */}
      <div className="rounded-lg border p-6">
        <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://dash.cloudflare.com"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
          >
            Cloudflare Dashboard
          </a>
          <a
            href="https://cloud.mongodb.com"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
          >
            MongoDB Atlas
          </a>
          <a
            href="https://dashboard.stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
          >
            Stripe Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
